import { FC } from 'react';
import type { ProseChange, ReviewDecision, SpreadsheetChange } from '@libs/types';
import { Box, Button, Chip, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';

import { RemediationDecision } from '@libs/constants';
import { type SingularKeys, useTrans } from '@libs/i18n';
import { CheckIcon, XIcon } from '@phosphor-icons/react';

type AnyChange = ProseChange | SpreadsheetChange;

const isProse = (c: AnyChange): c is ProseChange =>
  c.type === 'modification' || c.type === 'addition' || c.type === 'deletion';

/**
 * One proposed change, with the evidence needed to decide on it and the two
 * buttons that decide.
 *
 * The before/after pair is the whole point: the AI returns a document with every
 * change already applied, so without showing what the text *was*, "accept" is a
 * blind click. Deletions show only `before` (there is no after), additions only
 * `after`.
 */
export const RemediationChangeCard: FC<{
  change: AnyChange;
  disabled?: boolean;
  /** Only ACCEPTED/REJECTED are sendable — the API has no way to un-decide. */
  onDecide: (decision: ReviewDecision) => void;
}> = observer(({ change, disabled, onDecide }) => {
  const theme = useTheme();
  const { t } = useTrans();

  const decided = change.decision !== RemediationDecision.PENDING;
  const accepted = change.decision === RemediationDecision.ACCEPTED;

  const accent = !decided
    ? theme.palette.grey[300]
    : accepted
      ? theme.palette.green[500]
      : theme.palette.grey[400];

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        border: `1px solid ${accent}`,
        // A decided change stays readable but recedes, so the eye lands on what
        // still needs a decision.
        backgroundColor: decided
          ? alpha(theme.palette.grey[200], 0.4)
          : theme.palette.background.paper,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          size="small"
          label={t(`app.remediation.review.change.${change.type}` as SingularKeys)}
          sx={{ fontWeight: 600 }}
        />
        <Typography variant="textSM_semiBold" sx={{ color: 'text.primary' }}>
          {isProse(change) ? change.sectionName : change.header}
        </Typography>
        {change.containsPlaceholders && (
          <Chip
            size="small"
            label={t('app.remediation.review.placeholders')}
            sx={{
              backgroundColor: alpha(theme.palette.warning.main, 0.15),
              fontWeight: 600,
            }}
          />
        )}
      </Box>

      {isProse(change) ? (
        <ProseDiff change={change} />
      ) : (
        <Typography variant="textSM_regular" sx={{ color: 'text.secondary' }}>
          {change.columnKey}
          {change.beforeValuesCount != null && ` · ${change.beforeValuesCount}`}
        </Typography>
      )}

      {change.references.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
            {t('app.remediation.review.references')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {change.references.map((ref) => (
              <Chip key={`${ref.type}-${ref.key}`} size="small" label={ref.label} />
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button
          variant={accepted && decided ? 'primary' : 'secondary'}
          disabled={disabled}
          startIcon={<CheckIcon size={16} />}
          onClick={() => onDecide('ACCEPTED')}
        >
          {t(accepted && decided ? 'app.remediation.review.accepted' : 'app.remediation.review.accept')}
        </Button>
        <Button
          variant={!accepted && decided ? 'primary' : 'secondary'}
          disabled={disabled}
          startIcon={<XIcon size={16} />}
          onClick={() => onDecide('REJECTED')}
        >
          {t(
            !accepted && decided ? 'app.remediation.review.rejected' : 'app.remediation.review.reject',
          )}
        </Button>
      </Box>
    </Paper>
  );
});

const ProseDiff: FC<{ change: ProseChange }> = observer(({ change }) => {
  const theme = useTheme();
  const { t } = useTrans();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {change.before != null && change.before !== '' && (
        <DiffBlock
          label={t('app.remediation.review.before')}
          text={change.before}
          color={theme.palette.error.main}
          strike={change.type === 'deletion'}
        />
      )}
      {change.after != null && change.after !== '' && (
        <DiffBlock
          label={t('app.remediation.review.after')}
          text={change.after}
          color={theme.palette.green[700]}
        />
      )}
    </Box>
  );
});

const DiffBlock: FC<{ label: string; text: string; color: string; strike?: boolean }> = observer(
  ({ label, text, color, strike }) => (
    <Box
      sx={{
        borderInlineStart: `3px solid ${color}`,
        pl: 1.5,
        py: 0.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
      }}
    >
      <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
        {label}
      </Typography>
      <Typography
        variant="textSM_regular"
        sx={{
          color: 'text.primary',
          whiteSpace: 'pre-wrap',
          textDecoration: strike ? 'line-through' : 'none',
        }}
      >
        {text}
      </Typography>
    </Box>
  ),
);
